// La barra de acciones del visor de escritorio, que se convierte en una barra de prompt
// para editar sin salir del visor — ADR-2026-10-06-editar-en-el-visor.
//
// Antes "Editar" llevaba a otra pantalla (`/redes-sociales/editar-imagen`): un cambio
// visual grande para algo que casi siempre es escribir una frase. Ahora, al tocarlo, los
// botones se desvanecen y la MISMA caja (mismo vidrio, borde y radio) se estira hasta ser
// un campo de texto con "Editar". Al enviar se edita con prompt (`edit_general`, el mismo
// endpoint de la pantalla de editar), el visor muestra la imagen nueva y la barra queda
// lista para el próximo cambio. El círculo con el chevron (a la izquierda) o Esc vuelven a
// los botones.
//
// Desde ADR-2026-10-06-editar-con-referencias-y-escritor la pastilla adjunta: un botón
// "Añadir" abajo a la izquierda abre el selector de fuentes del Agente (productos, modelos,
// subidas), lo adjuntado se ve como miniaturas al lado, y la `@` lo menciona
// (ADR-2026-10-07-anadir-es-un-boton-en-la-barra-del-visor: antes eran las tres tarjetas de
// Redes arriba del texto; Matías las quiso como un solo botón). Al enviar viajan
// las referencias (con su mención), la CADENA de versiones del visor y `escribir_prompt`:
// el escritor del backend redacta el prompt, decide si hacen falta las vistas del
// producto de la imagen y desde qué versión conviene editar. Si eligió otra que la que se
// veía, la barra lo dice.
//
// Sólo la edición con prompt: precisa, cambiar producto y borrar objeto no entran (Matías).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, Plus, RefreshCw, X } from 'lucide-react';
import { BotonConCreditos } from './edicionEnElVisor/BotonConCreditos';
import { supabase } from '../lib/supabase';
import { MODO_UTILIZADO_EDICION, postImageEdit } from '../lib/imageEdit';
import { activarVersion, avisoDeBase, recortarCadena, type VersionParaEditar } from '../lib/versionesDelVisor';
import type { ChatAttachment } from '../pages/v2/agentAttachments';
import { nombreDeMencion } from '../pages/v2/mencionesDeAdjuntos';
import { etiquetaDeAdjunto, miniaturaDeAdjunto } from '../pages/v2/etiquetaDeAdjunto';
import { useReferenciasDeEdicion } from '../lib/useReferenciasDeEdicion';
import { ModalDeFuentes, type IdDeFuente } from '../chat-atlas/ui/ModalDeFuentes';
import { useMenusDeTexto } from '../chat-atlas/ui/useMenusDeTexto';

/** La fila nueva, con lo mismo que `fetchImages` lee de `imagenes_generadas`. */
export type FilaEditada = Record<string, unknown> & { id: string; file_name: string };

const COLUMNAS =
  'id, file_name, created_at, user_id, variante_id, variantes_ids, datos_generacion, formato, tipo, favorito, upscaled_path, variantes:variantes(nombre)';

/** La fila de la imagen editada, por el id o el nombre de archivo que devuelve el backend. */
export async function buscarFila(companyId: string, crudo: string): Promise<FilaEditada | null> {
  const base = () => supabase.from('imagenes_generadas').select(COLUMNAS).eq('id_company', companyId);
  const porId = await base().eq('id', crudo).maybeSingle();
  if (porId.data) return porId.data as unknown as FilaEditada;
  const porNombre = await base().eq('file_name', crudo).maybeSingle();
  if (porNombre.data) return porNombre.data as unknown as FilaEditada;
  const porPrefijo = await base().like('file_name', `${crudo}.%`).maybeSingle();
  return (porPrefijo.data as unknown as FilaEditada | null) ?? null;
}

/** Lo que cuesta una edición con prompt (lo mismo que muestra la pantalla de editar). */
const CREDITOS_POR_EDICION = 1;
/** Hasta cinco renglones; de ahí en más el texto scrollea adentro. */
const ALTO_MAXIMO_TEXTO = 5 * 22 + 18;

/** El textarea al alto de su texto (un renglón vacío, hasta `ALTO_MAXIMO_TEXTO`). */
function ajustarAlto(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, ALTO_MAXIMO_TEXTO)}px`;
  el.style.overflowY = el.scrollHeight > ALTO_MAXIMO_TEXTO ? 'auto' : 'hidden';
}

const BOTON = 'h-[var(--btn-h)] inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium text-[length:var(--text-body)] transition-colors';
const EASE = 'cubic-bezier(0.32,0.72,0,1)';
/** Tipografía y padding del texto: los comparten el textarea y la capa que resalta las
 *  `@menciones` detrás (tienen que calcar glifo por glifo). */
const CLASES_DEL_TEXTO = 'py-[9px] text-[15px] leading-[22px]';

type Aviso =
  | { tipo: 'error'; texto: string }
  | { tipo: 'regenerar'; parcial: boolean }
  /** El escritor editó desde otra versión que la que se veía. */
  | { tipo: 'base'; texto: string };

export function BarraDelVisor({
  children,
  abierta,
  promptOculto = false,
  conBotonVolver = true,
  onCerrar,
  imagenId,
  versiones,
  companyId,
  antesDeEditar,
  onEditando,
  onEditada,
  onEmpieza,
}: {
  /** Los botones de siempre (Editar, Carrusel, Animar, …). */
  children: ReactNode;
  /** En modo prompt. */
  abierta: boolean;
  /** Una herramienta del panel de edición está elegida (Recortar, Expandir, Mejorar): la
   *  pastilla y el círculo de volver se van con un fundido
   *  (ADR-2026-10-06-panel-de-edicion-en-el-visor). */
  promptOculto?: boolean;
  /** El círculo de volver a los botones. Sin él cuando se entró directo a editar (el atajo
   *  Editar): no hay a qué volver (ADR-2026-10-07-el-atajo-editar-abre-el-visor-2). */
  conBotonVolver?: boolean;
  onCerrar: () => void;
  /** La imagen que se está viendo (la que se edita). */
  imagenId: string;
  /** La cadena de versiones hasta la que se ve, en orden, cada una con la instrucción que
   *  la produjo. La lleva el visor (`cadenaParaEditar`); el escritor del backend decide
   *  desde cuál partir (ADR-2026-10-06-editar-con-referencias-y-escritor). */
  versiones: readonly VersionParaEditar[];
  companyId: string | null;
  /** Saldo y plan: `false` corta (el llamador ya mostró su aviso). */
  antesDeEditar: () => Promise<boolean>;
  /** Mientras genera, para que el visor muestre la imagen en espera. */
  onEditando?: (editando: boolean) => void;
  /** La edición terminó: el visor pasa a mostrar la imagen nueva. `baseId` es la versión
   *  desde la que se editó (la que se veía, salvo que el escritor eligiera otra). */
  onEditada: (fila: FilaEditada, origen: { instruccion: string; baseId: string }) => void;
  /** Arrancó una edición: el id de la fila nueva (o `null` si no hubo imagen). Para que
   *  quien prestó el visor la siga en su grilla aunque el visor se cierre. */
  onEmpieza?: (trabajo: Promise<string | null>, info: { baseId: string; creditos: number }) => void;
}) {
  const accionesRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [altoPrompt, setAltoPrompt] = useState<number | null>(null);
  const [medida, setMedida] = useState<{ ancho: number; alto: number } | null>(null);
  const [animando, setAnimando] = useState(false);
  const [instruccion, setInstruccion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  // Lo adjuntado en las tarjetas, y qué pestaña del selector está abierta.
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const [fuentes, setFuentes] = useState<IdDeFuente | null>(null);
  const referenciasDelBody = useReferenciasDeEdicion();

  // Los menús de la `@` (mencionar un adjunto) y el `#` (colores), como en Redes.
  const menus = useMenusDeTexto({
    valor: instruccion,
    onCambiarValor: setInstruccion,
    adjuntos,
    textareaRef: inputRef,
  });
  // Cuando la `@` inserta una mención, el texto cambia sin pasar por `onChange`: el alto
  // se vuelve a medir acá.
  useEffect(() => {
    if (inputRef.current) ajustarAlto(inputRef.current);
  }, [instruccion]);

  // El ancho natural de los botones: de ahí arranca y ahí vuelve la caja.
  useLayoutEffect(() => {
    const el = accionesRef.current;
    if (!el) return;
    const medir = () => setMedida({ ancho: el.offsetWidth, alto: el.offsetHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    // Y el alto del prompt, que es más alto que los botones: la caja crece hasta él.
    const form = formRef.current;
    const medirForm = () => form && setAltoPrompt(form.offsetHeight);
    medirForm();
    const roForm = new ResizeObserver(medirForm);
    if (form) roForm.observe(form);
    return () => {
      ro.disconnect();
      roForm.disconnect();
    };
  }, []);

  // Mientras la caja cambia de ancho recorta lo de adentro; quieta, deja salir el menú
  // de Herramientas, que abre hacia arriba.
  useEffect(() => {
    setAnimando(true);
    const t = window.setTimeout(() => setAnimando(false), 360);
    if (abierta) {
      const f = window.setTimeout(() => inputRef.current?.focus(), 160);
      // El alto del texto se mide de nuevo cuando la caja ya tiene su ancho final: medido
      // con la caja angosta (cerrada), el placeholder se partía en varios renglones y la
      // pastilla quedaba alta sin nada escrito.
      const a = window.setTimeout(() => inputRef.current && ajustarAlto(inputRef.current), 380);
      return () => {
        window.clearTimeout(t);
        window.clearTimeout(f);
        window.clearTimeout(a);
      };
    }
    setAviso(null);
    if (inputRef.current) {
      inputRef.current.style.height = '';
      inputRef.current.style.overflowY = 'hidden';
    }
    return () => window.clearTimeout(t);
  }, [abierta]);

  // Otra imagen (otra versión): el aviso y lo adjuntado eran de la anterior.
  useEffect(() => {
    setAviso(null);
    setAdjuntos([]);
  }, [imagenId]);

  const agregarAdjunto = (a: ChatAttachment) =>
    setAdjuntos((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
  const quitarAdjunto = (id: string) => setAdjuntos((prev) => prev.filter((a) => a.id !== id));

  const enviar = async (omitirChequeoRegenerar = false) => {
    const texto = instruccion.trim();
    if (!texto || enviando || !companyId) return;
    if (!(await antesDeEditar())) return;
    setEnviando(true);
    setAviso(null);
    onEditando?.(true);
    const adjuntosDeLaTanda = adjuntos;
    try {
      // El trabajo entero —pedir la edición, encontrar la fila nueva y activarla— es UNA
      // promesa que se entrega apenas arranca (`onEmpieza`): quien prestó el visor dibuja la
      // card de "Generando" en su grilla y pone el resultado aunque el visor se cierre a
      // mitad (ADR-2026-10-06-las-ediciones-del-visor-aterrizan-en-la-grilla). `null` = el
      // backend sugirió regenerar y no hay imagen.
      let regenerar: { parcial: boolean } | null = null;
      let base: { id: string; version?: number } = { id: imagenId };
      const trabajo = (async (): Promise<FilaEditada | null> => {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user) throw new Error('No se pudo identificar al usuario.');
        const referencias = await referenciasDelBody(adjuntosDeLaTanda, companyId);
        const data = await postImageEdit(
          {
            user_id: auth.user.id,
            imagen_id: imagenId,
            instruction: texto,
            mode: 'edit_general',
            // La cadena del visor y el escritor del backend
            // (ADR-2026-10-06-editar-con-referencias-y-escritor).
            versiones: recortarCadena(versiones).map((v) => ({
              imagen_id: v.imagenId,
              ...(v.instruccion ? { instruccion: v.instruccion } : {}),
            })),
            escribir_prompt: true,
            ...(referencias.length > 0 ? { reference_images: referencias } : {}),
            ...(omitirChequeoRegenerar ? { omitir_chequeo_regenerar: true } : {}),
            modo_utilizado: MODO_UTILIZADO_EDICION.edit_general ?? 'editar_imagen',
          },
          { companyId },
        );
        if (data?.response === 'sugerir_regenerar') {
          regenerar = { parcial: data.parcial === true };
          return null;
        }
        const crudo = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
        if (!crudo) throw new Error('No recibimos la imagen editada.');
        if (data.base_id) base = { id: String(data.base_id), version: data.base_version };
        const fila = await buscarFila(companyId, String(crudo));
        if (!fila?.file_name) throw new Error('No pudimos cargar la imagen editada.');
        // Cada versión del visor ya es una creación guardada (ADR-2026-10-06-panel-de-edicion-en-el-visor).
        await activarVersion(String(fila.id), companyId);
        return fila;
      })();
      onEmpieza?.(
        trabajo.then((f) => (f ? String(f.id) : null)),
        { baseId: imagenId, creditos: CREDITOS_POR_EDICION },
      );
      const fila = await trabajo;
      if (!fila) {
        setAviso({ tipo: 'regenerar', parcial: (regenerar as { parcial: boolean } | null)?.parcial === true });
        return;
      }
      setInstruccion('');
      // Lo adjuntado era para ESTA edición: la próxima arranca limpia.
      setAdjuntos((prev) => prev.filter((a) => !adjuntosDeLaTanda.some((x) => x.id === a.id)));
      if (inputRef.current) {
        inputRef.current.style.height = '';
        inputRef.current.style.overflowY = 'hidden';
      }
      setListo(true);
      window.setTimeout(() => setListo(false), 1400);
      if (base.id !== imagenId) setAviso({ tipo: 'base', texto: avisoDeBase(base.version) });
      onEditada(fila, { instruccion: texto, baseId: base.id });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error && e.message ? e.message : 'No pudimos editar la imagen. Probá de nuevo.' });
    } finally {
      setEnviando(false);
      onEditando?.(false);
    }
  };

  // El ancho deja lugar al círculo de volver (56 + 8) a la izquierda.
  /** El círculo de volver: sólo con la pastilla del prompt a la vista. */
  const conVolver = abierta && !promptOculto && conBotonVolver;
  const ancho = abierta ? 'min(640px, calc(100vw - 120px))' : medida ? `${medida.ancho}px` : undefined;

  return (
    <div className="relative">
      {/* Avisos, flotando arriba de la barra. */}
      {abierta && aviso && (
        // El centrado va afuera y la animación adentro: `fade-in` anima `transform` y
        // pisaría el `-translate-x-1/2`.
        <div className="absolute bottom-full left-1/2 mb-2 w-[min(640px,calc(100vw-120px))] -translate-x-1/2">
          <div className="animate-fade-in rounded-lg border border-white/[0.08] bg-black/60 px-4 py-3 text-white backdrop-blur-xl">
            {aviso.tipo === 'error' ? (
              <p className="text-[13px] text-white/90">{aviso.texto}</p>
            ) : aviso.tipo === 'base' ? (
              <div className="flex items-start justify-between gap-3">
                <p className="text-[13px] text-white/90">{aviso.texto}</p>
                <button
                  type="button"
                  onClick={() => setAviso(null)}
                  className="shrink-0 text-[12.5px] text-white/60 hover:text-white"
                >
                  Entendido
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-medium">La instrucción es poco precisa para editar</p>
                  <p className="mt-1 text-[12.5px] text-white/70">
                    Si el producto salió muy distinto, conviene volver a generar la imagen.
                    {aviso.parcial ? ' Parte de lo que pediste sí se puede editar.' : ''} No te descontamos ninguna imagen.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAviso(null);
                    void enviar(true);
                  }}
                  className={`${BOTON} bg-white/10 px-4 text-white hover:bg-white/20`}
                >
                  Editar igual
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Volver: un círculo aparte, a la izquierda de la pastilla, con el mismo vidrio.
          Entra después de que la caja empieza a estirarse. Sólo con la pastilla a la vista
          (sin herramienta elegida): con Recortar, Expandir o Mejorar se va con ella
          (ADR-2026-10-06-volver-solo-en-describir-edicion). */}
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Volver a las acciones"
        title="Volver (Esc)"
        tabIndex={conVolver ? 0 : -1}
        aria-hidden={!conVolver}
        className="absolute bottom-0 right-full mr-2 grid h-14 w-14 place-items-center rounded-full border border-white/[0.08] bg-white/[0.06] text-white/80 backdrop-blur-xl transition-colors hover:bg-white/[0.12] hover:text-white"
        style={{
          opacity: conVolver ? 1 : 0,
          transform: conVolver ? 'none' : 'translateX(12px) scale(0.85)',
          pointerEvents: conVolver ? 'auto' : 'none',
          transition: `opacity 220ms ease-out ${conVolver ? '160ms' : '0ms'}, transform 320ms ${EASE} ${conVolver ? '140ms' : '0ms'}, background-color 150ms`,
        }}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>

      {/* La caja: de barra de botones (radio de 8) a pastilla de prompt (radio de 28),
          animando ancho, alto y radio a la vez. */}
      <div
        aria-hidden={abierta && promptOculto}
        {...((abierta && promptOculto ? { inert: '' } : {}) as Record<string, unknown>)}
        // El mismo vidrio claro abierta o cerrada (ADR-2026-10-07-la-pastilla-de-editar-vuelve-al-vidrio-claro).
        // Sin el pulso azul.
        className={`relative border border-white/[0.08] bg-white/[0.06] backdrop-blur-xl ${abierta || animando ? 'overflow-hidden' : ''}`}
        style={{
          width: ancho,
          height: abierta && altoPrompt ? altoPrompt : medida?.alto,
          borderRadius: abierta ? 28 : 8,
          opacity: abierta && promptOculto ? 0 : 1,
          transform: abierta && promptOculto ? 'translateY(10px) scale(0.98)' : 'none',
          transition: `width 340ms ${EASE}, height 260ms ${EASE}, border-radius 340ms ${EASE}, background-color 260ms ease-out, opacity 220ms ease-out, transform 300ms ${EASE}`,
        }}
      >
        {/* Los botones. Absolutos para medir su ancho natural aunque la caja cambie. */}
        <div
          ref={accionesRef}
          aria-hidden={abierta}
          {...((abierta ? { inert: '' } : {}) as Record<string, unknown>)}
          className="absolute left-0 top-0 inline-flex items-center gap-2 whitespace-nowrap p-2"
          style={{
            opacity: abierta ? 0 : 1,
            transform: abierta ? 'translateY(6px) scale(0.98)' : 'none',
            transition: `opacity 180ms ease-out ${abierta ? '0ms' : '140ms'}, transform 260ms ${EASE} ${abierta ? '0ms' : '140ms'}`,
          }}
        >
          {children}
        </div>

        {/* El prompt: una sola pastilla con las tres tarjetas de referencia arriba, el texto
            (un renglón, crece con lo que se escribe hasta cinco) y "Editar" con su costo,
            como el Generar de Redes. Arriba y no abajo: la barra cuelga del borde de abajo,
            así que al crecer la caja sube. */}
        <form
          ref={formRef}
          aria-hidden={!abierta}
          {...((!abierta ? { inert: '' } : {}) as Record<string, unknown>)}
          onSubmit={(e) => {
            e.preventDefault();
            void enviar();
          }}
          className="absolute inset-x-0 top-0 flex flex-col gap-2 p-3 pt-2.5"
          style={{
            opacity: abierta ? 1 : 0,
            transform: abierta ? 'none' : 'translateY(6px)',
            transition: `opacity 200ms ease-out ${abierta ? '140ms' : '0ms'}, transform 300ms ${EASE} ${abierta ? '120ms' : '0ms'}`,
          }}
        >
          {/* El texto arriba, a lo ancho; abajo, "Añadir" a la izquierda con lo adjuntado como
              miniaturas, y "Editar" con su costo a la derecha
              (ADR-2026-10-07-anadir-es-un-boton-en-la-barra-del-visor). */}
          {/* La capa espejo (`menus.resaltado`) es `absolute inset-0`: cubre el padding de este
              envoltorio, así que lleva el MISMO `px` que él; si no, el texto visible queda corrido
              respecto del cursor del textarea. Si cambia uno, cambia el otro. */}
          <div className="relative min-w-0 px-2.5">
            {/* `relative`: detrás del textarea va la capa espejo que resalta las `@menciones`
                (el texto del textarea pasa a transparente; el cursor sigue siendo suyo), y
                los menús de la `@` y el `#` se apoyan sobre su borde de arriba. */}
            {menus.resaltado(`${CLASES_DEL_TEXTO} px-2.5 text-white`)}
            <textarea
              ref={inputRef}
              rows={1}
              value={instruccion}
              onScroll={menus.alScrollear}
              onChange={(e) => {
                setInstruccion(e.target.value);
                ajustarAlto(e.currentTarget);
                menus.releerToken(e.target);
              }}
              onSelect={(e) => menus.releerToken(e.currentTarget)}
              onBlur={(e) => menus.alDesenfocar(e)}
              onKeyDown={(e) => {
                if (menus.alTeclear(e)) return;
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCerrar();
                } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  // Enter edita; Shift+Enter baja de renglón.
                  e.preventDefault();
                  void enviar();
                }
              }}
              disabled={enviando}
              placeholder="¿Qué querés cambiar? Podés mencionar lo adjunto con @"
              aria-label="Qué querés cambiar de la imagen"
              className={`relative w-full resize-none bg-transparent ${CLASES_DEL_TEXTO} text-transparent caret-white outline-none placeholder:text-white/40 disabled:opacity-60`}
              style={{ maxHeight: ALTO_MAXIMO_TEXTO }}
            />
            {menus.menus}
          </div>
          <div className="flex items-center gap-2">
            {/* Añadir: abre el selector de fuentes del Agente (productos, modelos, subidas). */}
            <button
              type="button"
              onClick={() => setFuentes('productos')}
              disabled={enviando}
              className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-white/[0.08] pl-3 pr-4 text-[13.5px] font-medium text-white transition-colors hover:bg-white/[0.14] disabled:opacity-60"
            >
              <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              Añadir
            </button>
            {/* Lo adjuntado, como miniaturas con su X. Se mencionan con @ por su nombre. */}
            {adjuntos.length > 0 && (
              <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
                {adjuntos.map((a) => {
                  const mini = miniaturaDeAdjunto(a);
                  const nombre = nombreDeMencion(a, adjuntos);
                  return (
                    <span
                      key={a.id}
                      title={`@${nombre}`}
                      className="group relative inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white/[0.08] py-1 pl-1 pr-2"
                    >
                      {mini ? (
                        <img src={mini} alt="" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-[11px] text-white/70">{etiquetaDeAdjunto(a).slice(0, 1)}</span>
                      )}
                      <span className="max-w-[120px] truncate text-[12.5px] text-white/80">{nombre}</span>
                      <button
                        type="button"
                        onClick={() => quitarAdjunto(a.id)}
                        aria-label={`Quitar ${nombre}`}
                        className="grid h-5 w-5 place-items-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/25 hover:text-white"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <span className="flex-1" />
            <BotonConCreditos
              type="submit"
              creditos={CREDITOS_POR_EDICION}
              ocupado={enviando}
              listo={listo}
              disabled={!instruccion.trim()}
            >
              {enviando ? 'Editando…' : 'Editar'}
            </BotonConCreditos>
          </div>
        </form>
      </div>

      {/* El selector de fuentes del Agente (productos, modelos, subidas), portaleado al body:
          queda por encima del visor. Lo elegido entra a las tarjetas. */}
      <ModalDeFuentes
        abierto={fuentes !== null}
        onCerrar={() => setFuentes(null)}
        onAdjuntar={agregarAdjunto}
        multiple
        adjuntosActuales={adjuntos}
        onQuitar={quitarAdjunto}
        pestanaInicial={fuentes ?? 'productos'}
      />
    </div>
  );
}

export default BarraDelVisor;
